import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FiTrendingUp, FiThumbsUp, FiClock } from 'react-icons/fi';

const RecommendationsContainer = styled.div`
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid ${props => props.theme.colors.divider};
`;

const RecommendationsTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.colors.primary};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RecommendationsList = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const RecommendationCard = styled.div`
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const CardImage = styled.div`
  width: 100%;
  height: 160px;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
`;

const CardContent = styled.div`
  padding: 1rem;
`;

const CardTitle = styled.h4`
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  line-height: 1.3;
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: ${props => props.theme.colors.text};
  opacity: 0.8;
`;

const ReasonTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  background-color: ${props => {
    switch(props.type) {
      case 'trending': return 'rgba(29, 110, 60, 0.1)';
      case 'similar': return 'rgba(212, 160, 23, 0.1)';
      case 'recent': return 'rgba(0, 0, 0, 0.05)';
      default: return 'rgba(0, 0, 0, 0.05)';
    }
  }};
  color: ${props => {
    switch(props.type) {
      case 'trending': return props.theme.colors.primary;
      case 'similar': return props.theme.colors.accent;
      case 'recent': return props.theme.colors.text;
      default: return props.theme.colors.text;
    }
  }};
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  font-family: ${props => props.theme.fonts.accent};
  margin-bottom: 0.5rem;
`;

const ContentRecommendations = ({ currentArticle }) => {
  const [recommendations, setRecommendations] = useState([]);
  
  useEffect(() => {
    // In a real implementation, this would call an AI service to get personalized recommendations
    // For demo purposes, we'll create placeholder recommendations
    
    const generateRecommendations = () => {
      // Simulate different types of recommendations
      const trendingArticles = [
        {
          id: 'rec-1',
          title: 'School Board Approves Funding for New Athletic Facilities',
          imageUrl: 'https://source.unsplash.com/random/800x600/?school,sports',
          section: 'news',
          publishedAt: new Date().toISOString(),
          reason: 'trending'
        },
        {
          id: 'rec-2',
          title: 'Debate Team Advances to National Finals',
          imageUrl: 'https://source.unsplash.com/random/800x600/?debate,competition',
          section: 'news',
          publishedAt: new Date().toISOString(),
          reason: 'trending'
        }
      ];
      
      const similarArticles = [
        {
          id: 'rec-3',
          title: 'Basketball Team Prepares for Championship Game',
          imageUrl: 'https://source.unsplash.com/random/800x600/?basketball,team',
          section: 'sports',
          publishedAt: new Date().toISOString(),
          reason: 'similar'
        },
        {
          id: 'rec-4',
          title: 'Interview: Coach Johnson on Building Team Culture',
          imageUrl: 'https://source.unsplash.com/random/800x600/?coach,team',
          section: 'sports',
          publishedAt: new Date().toISOString(),
          reason: 'similar'
        }
      ];
      
      const recentArticles = [
        {
          id: 'rec-5',
          title: 'Student Government Announces New Initiatives',
          imageUrl: 'https://source.unsplash.com/random/800x600/?student,government',
          section: 'news',
          publishedAt: new Date().toISOString(),
          reason: 'recent'
        },
        {
          id: 'rec-6',
          title: 'Art Department Showcases Student Work',
          imageUrl: 'https://source.unsplash.com/random/800x600/?art,student',
          section: 'features',
          publishedAt: new Date().toISOString(),
          reason: 'recent'
        }
      ];
      
      // Combine and shuffle recommendations
      const allRecommendations = [...trendingArticles, ...similarArticles, ...recentArticles];
      return allRecommendations.sort(() => 0.5 - Math.random()).slice(0, 6);
    };
    
    setRecommendations(generateRecommendations());
  }, [currentArticle]);
  
  const getReasonIcon = (reason) => {
    switch(reason) {
      case 'trending': return <FiTrendingUp size={14} />;
      case 'similar': return <FiThumbsUp size={14} />;
      case 'recent': return <FiClock size={14} />;
      default: return null;
    }
  };
  
  const getReasonText = (reason) => {
    switch(reason) {
      case 'trending': return 'Trending';
      case 'similar': return 'Recommended for You';
      case 'recent': return 'Recently Published';
      default: return '';
    }
  };
  
  return (
    <RecommendationsContainer>
      <RecommendationsTitle>
        <FiTrendingUp size={20} /> Recommended for You
      </RecommendationsTitle>
      
      <RecommendationsList>
        {recommendations.map(article => (
          <RecommendationCard key={article.id}>
            <Link to={`/article/${article.id}`}>
              <CardImage src={article.imageUrl} />
              <CardContent>
                <ReasonTag type={article.reason}>
                  {getReasonIcon(article.reason)} {getReasonText(article.reason)}
                </ReasonTag>
                <CardTitle>{article.title}</CardTitle>
                <CardMeta>
                  {article.section.toUpperCase()} • {new Date(article.publishedAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </CardMeta>
              </CardContent>
            </Link>
          </RecommendationCard>
        ))}
      </RecommendationsList>
    </RecommendationsContainer>
  );
};

export default ContentRecommendations;